// Tenant User detail / lifecycle drawer.
//
// Mirrors UserDetailDrawer pattern but operates against the tenant_users
// collection + the CONSUMER Cognito pool via /tenants/{id}/users/* routes.
//
// Differences vs admin UserDetailDrawer:
//   - No role picker (V1 = always tenant_admin).
//   - No MFA reset action (consumer pool MFA reset isn't exposed in V1).
//   - No sign-out-all action (the suspend/anonymize paths handle it
//     server-side via adminGlobalSignOut).
//   - "Restore from removed" is replaced with "Anonymize (GDPR)" —
//     terminal, super_admin-only, type-to-confirm. The removed state is
//     a soft offboarding; anonymize is the terminal erasure.

import { useEffect, useState, type ReactNode } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Drawer,
  Spinner,
} from '@gc-coach/design-system';
import type { TenantId, TenantUserDetail } from '@gc-coach/shared';
import {
  anonymizeTenantUser,
  getTenantUser,
  reactivateTenantUser,
  removeTenantUser,
  resendTenantInvite,
  suspendTenantUser,
} from '../api/endpoints.js';
import { useSession } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import { formatRelativeTime } from '../users/time.js';
import styles from './TenantUserDetailDrawer.module.css';

export interface TenantUserDetailDrawerProps {
  tenantId: TenantId;
  tenantUserId: string;
  onClose: () => void;
  onChanged: () => void;
  /** Called after a soft remove OR an anonymize — both drop the row from default view. */
  onRemoved: () => void;
}

type ConfirmKind = null | 'suspend' | 'remove' | 'anonymize';

export function TenantUserDetailDrawer({
  tenantId,
  tenantUserId,
  onClose,
  onChanged,
  onRemoved,
}: TenantUserDetailDrawerProps) {
  const { t } = useI18n();
  const { session } = useSession();
  const isSuperAdmin = session?.roles.includes('super_admin') ?? false;

  const [user, setUser] = useState<TenantUserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionPending, setActionPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const [removeReason, setRemoveReason] = useState('');
  // Type-to-confirm value for GDPR anonymize.
  const [anonymizeConfirmText, setAnonymizeConfirmText] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const result = await getTenantUser(tenantId, tenantUserId);
      if (cancelled) return;
      if (result.ok) {
        setUser(result.data.user);
      } else {
        setError(t('tenantUsers.detailGenericError'));
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, tenantUserId, t]);

  async function runWithUpdate(
    op: () => Promise<
      { ok: true; data: { user: TenantUserDetail } } | { ok: false; code: string }
    >,
  ) {
    setActionPending(true);
    setError(null);
    const result = await op();
    setActionPending(false);
    if (result.ok) {
      setUser(result.data.user);
      onChanged();
    } else {
      setError(t('tenantUsers.detailGenericError'));
    }
  }

  async function onResendInvite() {
    if (!user) return;
    await runWithUpdate(() => resendTenantInvite(tenantId, user.id));
  }

  async function onSuspendConfirmed() {
    if (!user) return;
    setConfirm(null);
    await runWithUpdate(() => suspendTenantUser(tenantId, user.id));
  }

  async function onReactivate() {
    if (!user) return;
    await runWithUpdate(() => reactivateTenantUser(tenantId, user.id));
  }

  async function onRemoveConfirmed() {
    if (!user) return;
    const reason = removeReason.trim() || undefined;
    setConfirm(null);
    setRemoveReason('');
    setActionPending(true);
    setError(null);
    const result = await removeTenantUser(tenantId, user.id, reason);
    setActionPending(false);
    if (result.ok) {
      onRemoved();
    } else {
      setError(t('tenantUsers.detailGenericError'));
    }
  }

  async function onAnonymizeConfirmed() {
    if (!user) return;
    if (anonymizeConfirmText !== 'ANONYMIZE') return;
    setConfirm(null);
    setAnonymizeConfirmText('');
    setActionPending(true);
    setError(null);
    const result = await anonymizeTenantUser(tenantId, user.id);
    setActionPending(false);
    if (result.ok) {
      // Anonymize is terminal — drop the row from default view.
      onRemoved();
    } else if (result.code === 'FORBIDDEN') {
      setError(t('tenantUsers.detailAnonymizeForbidden'));
    } else {
      setError(t('tenantUsers.detailGenericError'));
    }
  }

  return (
    <Drawer
      open
      onClose={actionPending ? () => undefined : onClose}
      title={user ? user.displayName : ''}
      footer={
        user && (
          <div className={styles.dangerFooter}>
            {user.status === 'anonymized' ? (
              <span className={styles.terminalNote}>{t('tenantUsers.detailAnonymizedNote')}</span>
            ) : user.status === 'removed' ? (
              // Removed (soft) → only Anonymize (GDPR) is exposed.
              // Super_admin-only; non-super-admin sees disabled with tooltip.
              <Button
                kind="danger"
                onClick={() => setConfirm('anonymize')}
                disabled={actionPending || !isSuperAdmin}
                title={isSuperAdmin ? undefined : t('tenantUsers.detailAnonymizeSuperAdminOnly')}
              >
                {t('tenantUsers.detailAnonymize')}
              </Button>
            ) : (
              <>
                {user.status !== 'suspended' ? (
                  <Button
                    kind="ghost"
                    onClick={() => setConfirm('suspend')}
                    disabled={actionPending}
                  >
                    {t('tenantUsers.detailSuspend')}
                  </Button>
                ) : (
                  <Button kind="ghost" onClick={onReactivate} disabled={actionPending}>
                    {t('tenantUsers.detailReactivate')}
                  </Button>
                )}
                <Button
                  kind="danger"
                  onClick={() => setConfirm('remove')}
                  disabled={actionPending}
                >
                  {t('tenantUsers.detailRemove')}
                </Button>
              </>
            )}
          </div>
        )
      }
    >
      {isLoading && (
        <div className={styles.loadingState}>
          <Spinner />
        </div>
      )}

      {!isLoading && !user && (
        <Alert severity="danger">{t('tenantUsers.detailGenericError')}</Alert>
      )}

      {user && (
        <div className={styles.body}>
          {error && (
            <div className={styles.alert}>
              <Alert severity="danger">{error}</Alert>
            </div>
          )}

          <header className={styles.header}>
            <Avatar
              name={user.displayName}
              email={user.email}
              size="lg"
              variant={user.status === 'invited' ? 'dashed' : 'filled'}
            />
            <div className={styles.headerText}>
              <span className={styles.headerName}>{user.displayName}</span>
              <span className={styles.headerEmail}>{user.email}</span>
            </div>
            <span className={statusClass(user.status, styles)}>{statusText(user.status, t)}</span>
          </header>

          <dl className={styles.metaGrid}>
            <MetaCell label={t('tenantUsers.detailMetaRole')}>
              <span className={styles.mono}>{t('tenantUsers.roleTenantAdmin')}</span>
            </MetaCell>
            <MetaCell label={t('tenantUsers.detailMetaLocale')}>
              <span className={styles.mono}>{user.locale.toUpperCase()}</span>
            </MetaCell>
            <MetaCell label={t('tenantUsers.detailMetaMfa')}>
              <span>{renderMfa(user.mfaState, t)}</span>
            </MetaCell>
            <MetaCell label={t('tenantUsers.detailMetaTenure')}>
              <span>{formatRelativeTime(user.createdAt)}</span>
            </MetaCell>
            <MetaCell label={t('tenantUsers.detailMetaLastSeen')}>
              <span>
                {user.lastSeenAt
                  ? formatRelativeTime(user.lastSeenAt)
                  : t('tenantUsers.detailLastSeenNever')}
              </span>
            </MetaCell>
            <MetaCell label={t('tenantUsers.detailMetaCreatedBy')}>
              <span>{user.createdBy}</span>
            </MetaCell>
          </dl>

          {/* Actions */}
          {(user.status === 'active' || user.status === 'invited') && (
            <section className={styles.actions}>
              {user.status === 'invited' && (
                <Button kind="ghost" onClick={onResendInvite} disabled={actionPending}>
                  {t('tenantUsers.detailResendInvite')}
                </Button>
              )}
            </section>
          )}

          {confirm === 'suspend' && (
            <ConfirmPanel
              title={t('tenantUsers.detailConfirmSuspend')}
              body={t('tenantUsers.detailConfirmSuspendBody')}
              confirmLabel={t('tenantUsers.detailConfirmYes')}
              cancelLabel={t('tenantUsers.detailConfirmCancel')}
              onConfirm={onSuspendConfirmed}
              onCancel={() => setConfirm(null)}
              tone="warn"
            />
          )}
          {confirm === 'remove' && (
            <RemoveConfirmPanel
              title={t('tenantUsers.detailConfirmRemove')}
              body={t('tenantUsers.detailConfirmRemoveBody')}
              reasonLabel={t('tenantUsers.detailConfirmRemoveReasonLabel')}
              reasonPlaceholder={t('tenantUsers.detailConfirmRemoveReasonPlaceholder')}
              confirmLabel={t('tenantUsers.detailConfirmYes')}
              cancelLabel={t('tenantUsers.detailConfirmCancel')}
              reason={removeReason}
              setReason={setRemoveReason}
              onConfirm={onRemoveConfirmed}
              onCancel={() => {
                setConfirm(null);
                setRemoveReason('');
              }}
            />
          )}
          {confirm === 'anonymize' && (
            <AnonymizeConfirmPanel
              title={t('tenantUsers.detailConfirmAnonymize')}
              body={t('tenantUsers.detailConfirmAnonymizeBody')}
              typeLabel={t('tenantUsers.detailConfirmAnonymizeTypeLabel')}
              expectedToken="ANONYMIZE"
              confirmLabel={t('tenantUsers.detailAnonymize')}
              cancelLabel={t('tenantUsers.detailConfirmCancel')}
              value={anonymizeConfirmText}
              setValue={setAnonymizeConfirmText}
              onConfirm={onAnonymizeConfirmed}
              onCancel={() => {
                setConfirm(null);
                setAnonymizeConfirmText('');
              }}
            />
          )}
        </div>
      )}
    </Drawer>
  );
}

// ─── Subcomponents ───────────────────────────────────────────────────────────

function MetaCell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.metaCell}>
      <dt className={styles.metaLabel}>{label}</dt>
      <dd className={styles.metaValue}>{children}</dd>
    </div>
  );
}

function ConfirmPanel({
  title,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  tone,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  tone: 'warn' | 'danger';
}) {
  return (
    <div className={styles.confirm} role="dialog" aria-modal="true">
      <h3 className={styles.confirmTitle}>{title}</h3>
      <p className={styles.confirmBody}>{body}</p>
      <div className={styles.confirmButtons}>
        <Button kind="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button kind={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

function RemoveConfirmPanel({
  title,
  body,
  reasonLabel,
  reasonPlaceholder,
  confirmLabel,
  cancelLabel,
  reason,
  setReason,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  confirmLabel: string;
  cancelLabel: string;
  reason: string;
  setReason: (s: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={styles.confirm} role="dialog" aria-modal="true">
      <h3 className={styles.confirmTitle}>{title}</h3>
      <p className={styles.confirmBody}>{body}</p>
      <label className={styles.confirmLabel} htmlFor="remove-tenant-user-reason">
        {reasonLabel}
      </label>
      <textarea
        id="remove-tenant-user-reason"
        className={styles.confirmTextarea}
        rows={3}
        maxLength={500}
        value={reason}
        placeholder={reasonPlaceholder}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className={styles.confirmButtons}>
        <Button kind="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button kind="danger" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

function AnonymizeConfirmPanel({
  title,
  body,
  typeLabel,
  expectedToken,
  confirmLabel,
  cancelLabel,
  value,
  setValue,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  typeLabel: string;
  expectedToken: string;
  confirmLabel: string;
  cancelLabel: string;
  value: string;
  setValue: (s: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const enabled = value === expectedToken;
  return (
    <div className={styles.confirm} role="dialog" aria-modal="true">
      <h3 className={styles.confirmTitle}>{title}</h3>
      <p className={styles.confirmBody}>{body}</p>
      <label className={styles.confirmLabel} htmlFor="anonymize-confirm">
        {typeLabel}
      </label>
      <input
        id="anonymize-confirm"
        type="text"
        className={styles.confirmInput}
        value={value}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => setValue(e.target.value)}
      />
      <div className={styles.confirmButtons}>
        <Button kind="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button kind="danger" onClick={onConfirm} disabled={!enabled}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

function renderMfa(state: TenantUserDetail['mfaState'], t: (k: string) => string): string {
  if (state === 'enabled') return t('tenantUsers.detailMfaEnabled');
  if (state === 'pending') return t('tenantUsers.detailMfaPending');
  return t('tenantUsers.detailMfaNone');
}

function statusClass(s: TenantUserDetail['status'], cls: Record<string, string>): string {
  if (s === 'active') return cls['statusActive'] ?? '';
  if (s === 'invited') return cls['statusInvited'] ?? '';
  if (s === 'removed') return cls['statusRemoved'] ?? cls['statusSuspended'] ?? '';
  if (s === 'anonymized') return cls['statusRemoved'] ?? cls['statusSuspended'] ?? '';
  return cls['statusSuspended'] ?? '';
}

function statusText(s: TenantUserDetail['status'], t: (k: string) => string): string {
  if (s === 'active') return t('tenantUsers.statusActive');
  if (s === 'invited') return t('tenantUsers.statusInvited');
  if (s === 'suspended') return t('tenantUsers.statusSuspended');
  if (s === 'removed') return t('tenantUsers.statusRemoved');
  return t('tenantUsers.statusAnonymized');
}
